import React from 'react';
import { FileText, DollarSign, Calendar, TrendingUp } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const IncomeTax: React.FC = () => {
  useSEO({
    title: 'Imposto de Renda 2026 (IRPF): Como Declarar Investimentos e Tabela | O Teu Dinheiro',
    description: 'Guia completo do Imposto de Renda 2026: nova faixa de isenção, como declarar ações, FIIs, CDB, LCI/LCA e deduções legais para aumentar sua restituição.',
    keywords: 'Imposto de Renda 2026, IRPF 2026, como declarar investimentos, tabela imposto de renda 2026, restituição IRPF',
    url: 'https://www.oteudinheiro.com.br/imposto-renda'
  });
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-600 to-amber-700 text-white py-20">
        <div className="container-custom text-center">
          <FileText className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Imposto de Renda
          </h1>
          <p className="text-xl md:text-2xl text-amber-100 max-w-3xl mx-auto">
            Como declarar investimentos e otimizar sua declaração
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                O que é o Imposto de Renda?
              </h2>

              <div className="prose prose-lg text-gray-600 mb-8">
                <p className="mb-4">
                  O Imposto de Renda (IR) é um tributo federal que incide sobre a renda
                  e os proventos de qualquer natureza de pessoas físicas e jurídicas.
                </p>
                <p className="mb-4">
                  Para pessoas físicas, a declaração anual é obrigatória para quem
                  recebeu rendimentos tributáveis acima de R$ 33.888,00 em 2024.
                </p>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Quem deve declarar
              </h3>

              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <div className="bg-amber-100 p-2 rounded-lg">
                    <DollarSign className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Rendimentos altos</h4>
                    <p className="text-gray-600 text-sm">Quem recebeu mais de R$ 33.888,00</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-amber-100 p-2 rounded-lg">
                    <Calendar className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Investimentos</h4>
                    <p className="text-gray-600 text-sm">Quem teve ganho de capital em investimentos</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-amber-100 p-2 rounded-lg">
                    <FileText className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Bens e direitos</h4>
                    <p className="text-gray-600 text-sm">Quem possui bens acima de R$ 300.000</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Principais mudanças em 2025
                </h3>

                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h4 className="font-semibold text-amber-900 mb-2">Nova Tabela Progressiva</h4>
                    <p className="text-amber-700 text-sm">
                      Isenção para rendimentos até R$ 3.036,00/mês. Alíquotas de 7,5% a 27,5%.
                    </p>
                  </div>

                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h4 className="font-semibold text-amber-900 mb-2">Rendimentos no Exterior</h4>
                    <p className="text-amber-700 text-sm">
                      Dividendos do exterior: 15% de IR, com possibilidade de compensação.
                    </p>
                  </div>

                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h4 className="font-semibold text-amber-900 mb-2">App Meu IR</h4>
                    <p className="text-amber-700 text-sm">
                      Novo aplicativo para acompanhar status da declaração e pendências.
                    </p>
                  </div>

                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h4 className="font-semibold text-amber-900 mb-2">Restituições 2025</h4>
                    <p className="text-amber-700 text-sm">
                      5 lotes de maio a setembro, com prioridade para idosos e PIX.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Dates Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Prazos e datas importantes 2025
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
              <h3 className="text-xl font-semibold text-amber-900 mb-4 flex items-center">
                <Calendar className="w-5 h-5 text-amber-600 mr-2" />
                Período de Declaração
              </h3>
              <ul className="text-amber-700 space-y-2">
                <li>• <strong>Início:</strong> 17 de março de 2025</li>
                <li>• <strong>Prazo final:</strong> 31 de maio de 2025</li>
                <li>• <strong>Multa por atraso:</strong> 1% do imposto devido (mín. R$ 165,74)</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-xl p-6 border border-green-200">
              <h3 className="text-xl font-semibold text-green-900 mb-4 flex items-center">
                <TrendingUp className="w-5 h-5 text-green-600 mr-2" />
                Restituições 2025
              </h3>
              <ul className="text-green-700 space-y-2">
                <li>• <strong>1º lote:</strong> 30 de maio</li>
                <li>• <strong>2º lote:</strong> 30 de junho</li>
                <li>• <strong>3º lote:</strong> 31 de julho</li>
                <li>• <strong>4º lote:</strong> 29 de agosto</li>
                <li>• <strong>5º lote:</strong> 30 de setembro</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Section */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Como declarar investimentos
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <DollarSign className="w-5 h-5 text-amber-600 mr-2" />
                Renda Fixa
              </h3>
              <ul className="text-gray-600 space-y-2">
                <li>• CDBs: Declarar no campo "Rendimentos de aplicações financeiras"</li>
                <li>• Tesouro Direto: Mesmo campo, com retenção na fonte</li>
                <li>• LCI/LCA: Isentos de IR, mas declarar no campo específico</li>
                <li>• Poupança: Isenta, mas declarar se recebeu mais de R$ 40.000</li>
                <li>• Investimentos no exterior: 15% de IR, declarar anualmente</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <TrendingUp className="w-5 h-5 text-amber-600 mr-2" />
                Renda Variável
              </h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Ações: Declarar ganhos de capital no campo específico</li>
                <li>• FIIs: Dividendos isentos, mas declarar no campo de rendimentos isentos</li>
                <li>• Fundos: Declarar resgates e come-cotas</li>
                <li>• Criptomoedas: Declarar ganhos de capital (obrigatório desde 2024)</li>
                <li>• Ações no exterior: 15% de IR sobre dividendos e ganhos</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Dicas para uma boa declaração
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Organize os documentos
              </h3>
              <p className="text-secondary-700">
                Tenha todos os comprovantes de rendimentos, investimentos
                e despesas dedutíveis organizados antes de começar.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Use o app Meu IR
              </h3>
              <p className="text-secondary-700">
                Baixe o aplicativo "Meu Imposto de Renda" para acompanhar
                o status da declaração e verificar pendências.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Declare tudo
              </h3>
              <p className="text-secondary-700">
                Não omita informações. A Receita Federal tem acesso
                a dados bancários e pode identificar inconsistências.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Investimentos no exterior
              </h3>
              <p className="text-secondary-700">
                Declare rendimentos do exterior (15% de IR). Você pode
                compensar impostos pagos no exterior.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Guarde a declaração
              </h3>
              <p className="text-secondary-700">
                Mantenha cópia da declaração por 5 anos. A Receita
                pode pedir esclarecimentos posteriormente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-amber-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-6">
            Declare seu IR corretamente!
          </h2>
          <p className="text-xl text-amber-100 mb-8 max-w-2xl mx-auto">
            Uma declaração bem feita evita problemas e pode até gerar restituição.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/isencoes"
              className="btn bg-white text-amber-600 hover:bg-gray-100"
            >
              Ver Isenções
            </a>
            <a
              href="/calculadoras"
              className="btn bg-transparent text-white border-2 border-white hover:bg-white hover:text-amber-600"
            >
              Calcular IR
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IncomeTax;
